import { ReactNode, useState, useRef, useEffect, useCallback } from 'react';
import { registerKeyHandler, unregisterKeyHandler, setFocusMode } from 't-lib-ui/structure';
import ShortcutsOverlay from './ShortcutsOverlay';
import './ThreeColumnLayout.css';

interface ThreeColumnLayoutProps {
  // isDrawer: the sidebar is an overlay drawer (mobile and narrow screens) rather than a permanent column.
  left: ReactNode | ((props: { onCollapse: () => void; onMenuClick?: (options?: { isReselect?: boolean }) => void; isMobile: boolean; isDrawer: boolean }) => ReactNode);
  middle?: ReactNode | ((props: { showExpandButton: boolean; onExpand: () => void; onArticleSelect?: () => void; isMobile: boolean }) => ReactNode);
  right: ReactNode | ((props: { showExpandButton: boolean; onExpand: () => void; isMobile: boolean }) => ReactNode);
  dialog?: ReactNode | ((props: { isMobile: boolean; onExpand: () => void }) => ReactNode);
  showMiddle?: boolean;
  showLeft?: boolean;
  startWithLeftCollapsed?: boolean;
  onArticleSelect?: () => void;
}

const STORAGE_KEY_LEFT_WIDTH_NARROW = 'feed-layout-left-width-narrow';
const STORAGE_KEY_MIDDLE_WIDTH_NARROW = 'feed-layout-middle-width-narrow';
const STORAGE_KEY_LEFT_WIDTH_FULL = 'feed-layout-left-width-full';
const STORAGE_KEY_MIDDLE_WIDTH_FULL = 'feed-layout-middle-width-full';
const MIN_COLUMN_WIDTH = 200;
const MIN_RIGHT_COLUMN_WIDTH = 350;
const NARROW_SCREEN_BREAKPOINT = 1240;
const MOBILE_SCREEN_BREAKPOINT = 750;

// First keyboard-navigable item in the sidebar — focused when the sidebar is opened via keyboard.
const FIRST_SIDEBAR_ITEM = '.leftSidebar .flowFocussable:not(.sidewayFocus):not(.touchOnly)';

// Mobile widths (hardcoded)
const MOBILE_LEFT_WIDTH_PCT = 0.85;

// Narrow defaults
const NARROW_DEFAULT_LEFT_WIDTH = 240;
const NARROW_DEFAULT_MIDDLE_WIDTH = 260;

// Full defaults (as percentages and minimums)
const FULL_DEFAULT_LEFT_WIDTH_PCT = 0.20;
const FULL_DEFAULT_LEFT_WIDTH_MIN = 280;
const FULL_DEFAULT_MIDDLE_WIDTH_PCT = 0.30;
const FULL_DEFAULT_MIDDLE_WIDTH_MIN = 320;

// Constraints
const NARROW_MIDDLE_MAX_PCT = 0.50;
const FULL_LEFT_MAX_PCT = 0.25;

/** Scroll the sidebar so the element is vertically centered (no-op if element isn't in .leftSidebar) */
function scrollSidebarToCenter(el: HTMLElement) {
  const sidebar = el.closest('.leftSidebar') as HTMLElement | null;
  if (!sidebar) return;
  const sidebarRect = sidebar.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const offset = (elRect.top + elRect.bottom) / 2 - (sidebarRect.top + sidebarRect.bottom) / 2;
  if (Math.abs(offset) > 10)
    sidebar.scrollBy({ top: offset, behavior: 'smooth' });
}

const getResolutionMode = (width: number): 'mobile' | 'narrow' | 'full' => {
  if (width <= MOBILE_SCREEN_BREAKPOINT) return 'mobile';
  if (width <= NARROW_SCREEN_BREAKPOINT) return 'narrow';
  return 'full';
};

const getDefaultLeftWidth = (windowWidth: number, mode: 'mobile' | 'narrow' | 'full'): number => {
  if (mode == 'mobile') return Math.floor(windowWidth * MOBILE_LEFT_WIDTH_PCT);
  if (mode == 'narrow') return NARROW_DEFAULT_LEFT_WIDTH;
  return Math.max(FULL_DEFAULT_LEFT_WIDTH_MIN, Math.floor(windowWidth * FULL_DEFAULT_LEFT_WIDTH_PCT));
};

const getDefaultMiddleWidth = (windowWidth: number, mode: 'mobile' | 'narrow' | 'full'): number => {
  if (mode == 'mobile') return 0;
  if (mode == 'narrow') return NARROW_DEFAULT_MIDDLE_WIDTH;
  return Math.max(FULL_DEFAULT_MIDDLE_WIDTH_MIN, Math.floor(windowWidth * FULL_DEFAULT_MIDDLE_WIDTH_PCT));
};

const constrainLeftWidth = (width: number, windowWidth: number, mode: 'mobile' | 'narrow' | 'full'): number => {
  if (mode == 'mobile') return Math.floor(windowWidth * MOBILE_LEFT_WIDTH_PCT);
  if (mode == 'narrow') return Math.max(MIN_COLUMN_WIDTH, width);
  const maxWidth = Math.floor(windowWidth * FULL_LEFT_MAX_PCT);
  return Math.max(MIN_COLUMN_WIDTH, Math.min(width, maxWidth));
};

const constrainMiddleWidth = (width: number, windowWidth: number, leftWidth: number, mode: 'mobile' | 'narrow' | 'full'): number => {
  if (mode == 'mobile') return 0;
  const rightMax = windowWidth - leftWidth - MIN_RIGHT_COLUMN_WIDTH;
  if (mode == 'narrow') {
    const maxWidth = Math.min(Math.floor(windowWidth * NARROW_MIDDLE_MAX_PCT), rightMax);
    return Math.max(MIN_COLUMN_WIDTH, Math.min(width, maxWidth));
  }
  // Full mode
  const leftPct = leftWidth / windowWidth;
  const maxPct = 0.50 - leftPct;
  const maxWidth = Math.min(Math.floor(windowWidth * maxPct), rightMax);
  return Math.max(MIN_COLUMN_WIDTH, Math.min(width, maxWidth));
};

// Module-level flag: when navigation to a feed is triggered (sidebar, "Next Feed",
// HomePage, feed tile) on a narrow or mobile screen, the next ThreeColumnLayout mount
// should start with the sidebar collapsed so it opens straight into the content.
let startCollapsed = false;

export function requestStartCollapsed() {
  if (typeof window !== 'undefined' && window.innerWidth <= NARROW_SCREEN_BREAKPOINT)
    startCollapsed = true;
}

const ThreeColumnLayout: React.FC<ThreeColumnLayoutProps> = ({
  left,
  middle,
  right,
  dialog,
  showMiddle = true,
  showLeft = true,
  startWithLeftCollapsed = false,
  onArticleSelect,
}) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const isNarrowScreen = windowWidth <= NARROW_SCREEN_BREAKPOINT;
  const isMobileScreen = windowWidth <= MOBILE_SCREEN_BREAKPOINT;
  const resolutionMode = getResolutionMode(windowWidth);

  const [leftWidth, setLeftWidth] = useState(() => {
    const mode = getResolutionMode(typeof window !== 'undefined' ? window.innerWidth : 1920);
    const width = typeof window !== 'undefined' ? window.innerWidth : 1920;

    if (mode == 'mobile') {
      return Math.floor(width * MOBILE_LEFT_WIDTH_PCT);
    }

    const storageKey = mode == 'narrow' ? STORAGE_KEY_LEFT_WIDTH_NARROW : STORAGE_KEY_LEFT_WIDTH_FULL;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      const parsedWidth = parseInt(stored, 10);
      return constrainLeftWidth(parsedWidth, width, mode);
    }

    return getDefaultLeftWidth(width, mode);
  });

  const [middleWidth, setMiddleWidth] = useState(() => {
    const mode = getResolutionMode(typeof window !== 'undefined' ? window.innerWidth : 1920);
    const width = typeof window !== 'undefined' ? window.innerWidth : 1920;

    if (mode == 'mobile') {
      return 0;
    }

    const storageKey = mode == 'narrow' ? STORAGE_KEY_MIDDLE_WIDTH_NARROW : STORAGE_KEY_MIDDLE_WIDTH_FULL;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      const parsedWidth = parseInt(stored, 10);
      return constrainMiddleWidth(parsedWidth, width, leftWidth, mode);
    }

    return getDefaultMiddleWidth(width, mode);
  });
  
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(() => {
    if (!showLeft) return true;
    if (startWithLeftCollapsed) return true;
    if (startCollapsed) { startCollapsed = false; return true; }
    // Drawer mode (mobile and narrow): the sidebar is an overlay that starts closed, so every page
    // and feed opens straight into its content (the user opens the drawer via the menu button).
    // Without this, mobile pages — Home, Help, Read-later, Preferences, Account — would mount (and
    // reload) with the sidebar covering the content.
    const w = typeof window !== 'undefined' ? window.innerWidth : 1920;
    if (w <= NARROW_SCREEN_BREAKPOINT) return true;
    return false;
  });
  // The right column is always shown in drawer mode (the sidebar overlays instead of taking
  // space); the resolution effect manages it for desktop.
  const [isRightCollapsed, setIsRightCollapsed] = useState(false);
  const [isMiddleCollapsed, setIsMiddleCollapsed] = useState(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
    return width <= MOBILE_SCREEN_BREAKPOINT; // Hide middle on mobile
  });
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingMiddle, setIsDraggingMiddle] = useState(false);
  const [leftGhostPosition, setLeftGhostPosition] = useState<number | null>(null);
  const [middleGhostPosition, setMiddleGhostPosition] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const leftResizeRef = useRef<HTMLDivElement>(null);
  const middleResizeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Don't store mobile widths (they're calculated)
    if (resolutionMode == 'mobile') return;

    const storageKey = resolutionMode == 'narrow' ? STORAGE_KEY_LEFT_WIDTH_NARROW : STORAGE_KEY_LEFT_WIDTH_FULL;
    localStorage.setItem(storageKey, leftWidth.toString());
  }, [leftWidth, resolutionMode]);

  useEffect(() => {
    // Don't store mobile widths (they're calculated)
    if (resolutionMode == 'mobile') return;

    const storageKey = resolutionMode == 'narrow' ? STORAGE_KEY_MIDDLE_WIDTH_NARROW : STORAGE_KEY_MIDDLE_WIDTH_FULL;
    localStorage.setItem(storageKey, middleWidth.toString());
  }, [middleWidth, resolutionMode]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle resolution mode changes
  useEffect(() => {
    const newMode = getResolutionMode(windowWidth);

    if (newMode == 'mobile') {
      // Mobile: use calculated width
      setLeftWidth(Math.floor(windowWidth * MOBILE_LEFT_WIDTH_PCT));
      setMiddleWidth(0);
    } else {
      // Narrow or Full: load from storage or use defaults, then constrain
      const leftStorageKey = newMode == 'narrow' ? STORAGE_KEY_LEFT_WIDTH_NARROW : STORAGE_KEY_LEFT_WIDTH_FULL;
      const middleStorageKey = newMode == 'narrow' ? STORAGE_KEY_MIDDLE_WIDTH_NARROW : STORAGE_KEY_MIDDLE_WIDTH_FULL;

      const storedLeft = localStorage.getItem(leftStorageKey);
      const storedMiddle = localStorage.getItem(middleStorageKey);

      const newLeftWidth = storedLeft
        ? constrainLeftWidth(parseInt(storedLeft, 10), windowWidth, newMode)
        : getDefaultLeftWidth(windowWidth, newMode);

      const newMiddleWidth = storedMiddle
        ? constrainMiddleWidth(parseInt(storedMiddle, 10), windowWidth, newLeftWidth, newMode)
        : getDefaultMiddleWidth(windowWidth, newMode);

      setLeftWidth(newLeftWidth);
      setMiddleWidth(newMiddleWidth);
    }
  }, [resolutionMode, windowWidth]);

  // Track previous screen mode to detect actual screen size changes (not initial mount)
  const prevScreenMode = useRef<'mobile' | 'narrow' | 'full' | null>(null);
  const hasMiddle = !!middle;

  useEffect(() => {
    const currentMode = isMobileScreen ? 'mobile' : (isNarrowScreen ? 'narrow' : 'full');
    const isScreenModeChange = prevScreenMode.current !== null && prevScreenMode.current !== currentMode;

    if (isMobileScreen) {
      // Mobile is drawer mode (like narrow): the sidebar overlays the content and must never open on
      // its own — only the menu button opens it. Entering mobile from another mode closes the drawer
      // so a reload/resize lands on the content, not the menu.
      if (isScreenModeChange) setIsLeftCollapsed(true);
      setIsRightCollapsed(false);
      setIsMiddleCollapsed(true);
    } else if (isNarrowScreen) {
      // Narrow ("medium") behaves like mobile: the sidebar is an overlay drawer and the content
      // fills the full width (HeadlineList + PostView for full feeds, PostView otherwise).
      if (isScreenModeChange) setIsLeftCollapsed(true); // entering narrow closes the drawer
      setIsRightCollapsed(false);
      setIsMiddleCollapsed(false);
    } else {
      // Desktop mode: show all
      setIsRightCollapsed(false);
      setIsMiddleCollapsed(false);
    }
    prevScreenMode.current = currentMode;
  }, [isNarrowScreen, isMobileScreen, showMiddle, hasMiddle]);

  const handleLeftResizeStart = (ev: React.PointerEvent<HTMLDivElement>) => {
    ev.preventDefault(); // Prevent default touch behaviors like scrolling

    const target = ev.currentTarget;
    const pointerId = ev.pointerId;

    target.setPointerCapture(pointerId);
    setIsDraggingLeft(true);

    const startX = ev.clientX;
    const startWidth = leftWidth;
    let finalWidth = startWidth;

    const maxLeftWidth = windowWidth - middleWidth - MIN_RIGHT_COLUMN_WIDTH;
    const handleMove = (moveEv: PointerEvent) => {
      moveEv.preventDefault(); // Prevent scrolling during drag
      const delta = moveEv.clientX - startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, Math.min(startWidth + delta, maxLeftWidth));
      finalWidth = newWidth;
      // Only update ghost position during drag, not actual width
      setLeftGhostPosition(newWidth);
    };

    const handleUp = () => {
      // Apply the final width on release
      setLeftWidth(finalWidth);
      setLeftGhostPosition(null);
      setIsDraggingLeft(false);
      if (target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
      }
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleUp);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleUp);
  };

  const handleMiddleResizeStart = (ev: React.PointerEvent<HTMLDivElement>) => {
    ev.preventDefault(); // Prevent default touch behaviors like scrolling

    const target = ev.currentTarget;
    const pointerId = ev.pointerId;

    target.setPointerCapture(pointerId);
    setIsDraggingMiddle(true);

    const startX = ev.clientX;
    const startWidth = middleWidth;
    let finalWidth = startWidth;

    const maxMiddleWidth = windowWidth - leftWidth - MIN_RIGHT_COLUMN_WIDTH;
    const handleMove = (moveEv: PointerEvent) => {
      moveEv.preventDefault(); // Prevent scrolling during drag
      const delta = moveEv.clientX - startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, Math.min(startWidth + delta, maxMiddleWidth));
      finalWidth = newWidth;
      // Only update ghost position during drag, not actual width
      setMiddleGhostPosition(newWidth);
    };

    const handleUp = () => {
      // Apply the final width on release
      setMiddleWidth(finalWidth);
      setMiddleGhostPosition(null);
      setIsDraggingMiddle(false);
      if (target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
      }
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleUp);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleUp);
  };

  // Pending focus after layout change (sidebar collapse/expand, right column show/hide)
  const pendingFocusRef = useRef<string | null>(null);

  const toggleLeftCollapse = useCallback(() => {
    // In drawer mode (mobile/narrow) the sidebar overlays the content; on desktop it's a column.
    // In all cases we just toggle the sidebar's visibility.
    setIsLeftCollapsed(prev => {
      // When opening as the result of keyboard interaction (focus mode is "visible"), land focus
      // on the first sidebar item. Mouse opens leave focus where it is (focus mode "hidden").
      if (prev && document.querySelector('.flowForm.visibleFocus, .flowFormModal.visibleFocus'))
        pendingFocusRef.current = FIRST_SIDEBAR_ITEM;
      return !prev;
    });
  }, []);

  // ESC toggles the left sidebar: shows it when collapsed, hides it when open.
  useEffect(() => {
    registerKeyHandler('threeColumnEscape', {
      'Escape': () => {
        // Esc is a keyboard action, so opening this way always lands focus on the first item.
        if (isLeftCollapsed) { setFocusMode('visible'); toggleLeftCollapse(); return true; }
        // Closing via Escape only applies in narrow/mobile drawer mode, where the sidebar is an
        // overlay. On desktop it's a permanent column and must not collapse on Escape.
        if (!isNarrowScreen) return false;
        // Don't steal Escape from a field being edited inside it (feed filter, folder
        // create/rename all clear/cancel on their own Escape).
        const ae = document.activeElement as HTMLElement | null;
        const tag = ae?.tagName;
        if (tag == 'INPUT' || tag == 'TEXTAREA' || ae?.isContentEditable) return false;
        toggleLeftCollapse();
        return true;
      },
    });
    return () => unregisterKeyHandler('threeColumnEscape');
  }, [isLeftCollapsed, isNarrowScreen, toggleLeftCollapse]);

  // Apply pending focus after a layout change (sidebar collapse/expand, right column show/hide).
  useEffect(() => {
    if (!pendingFocusRef.current) return;
    const selector = pendingFocusRef.current;
    requestAnimationFrame(() => {
      const candidates = Array.from(document.querySelectorAll<HTMLElement>(selector));
      for (const el of candidates) {
        if (el.offsetWidth > 0) {
          pendingFocusRef.current = null;
          setFocusMode('visible');
          el.focus();
          scrollSidebarToCenter(el);
          return;
        }
      }
    });
  }, [isLeftCollapsed, isRightCollapsed]);

  // Alt+m / Alt+f / Alt+c navigation shortcuts
  useEffect(() => {
    const showSidebarAndFocus = (selector: string): boolean => {
      setFocusMode('visible');
      if (!isLeftCollapsed) {
        const target = document.querySelector<HTMLElement>(selector);
        if (target) {
          target.focus();
          scrollSidebarToCenter(target);
        }
        return true;
      }
      pendingFocusRef.current = selector;
      setIsLeftCollapsed(false);
      if (isNarrowScreen && !isMobileScreen) setIsRightCollapsed(true);
      return true;
    };

    registerKeyHandler('altNavShortcuts', {
      'Alt+m': () => showSidebarAndFocus('.leftSidebar .flatItem.flowFocussable'),
      'Alt+f': () => showSidebarAndFocus('.leftSidebar .feedItem.flowFocussable'),
      'Alt+c': () => {
        setFocusMode('visible');
        const candidates = Array.from(document.querySelectorAll<HTMLElement>('.postView .optionsMenuContainer .actionButton.flowFocussable, .postView .mobileMenuButton.flowFocussable'));
        for (const el of candidates) {
          if (el.offsetWidth > 0) { el.focus(); return true; }
        }
        // PostView not visible (narrow/mobile with sidebar open) - switch to show it
        if (isNarrowScreen || isMobileScreen) {
          pendingFocusRef.current = '.postView .optionsMenuContainer .actionButton.flowFocussable, .postView .mobileMenuButton.flowFocussable';
          setIsLeftCollapsed(true);
          setIsRightCollapsed(false);
          if (isMobileScreen) setIsMiddleCollapsed(true);
          return true;
        }
        return false;
      },
    });
    return () => unregisterKeyHandler('altNavShortcuts');
  }, [isLeftCollapsed, isNarrowScreen, isMobileScreen]);

  // Shortcuts overlay: press "?" to show it, then any key to dismiss. (Alt-hold,
  // the old trigger, is unreliable on Windows — it activates the menu bar.)
  const [showShortcutsOverlay, setShowShortcutsOverlay] = useState(false);
  const shortcutsShownRef = useRef(false);
  shortcutsShownRef.current = showShortcutsOverlay;
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase();
      return tag == 'input' || tag == 'textarea' || !!(el as HTMLElement | null)?.isContentEditable;
    };
    const handleKeyDown = (ev: KeyboardEvent) => {
      if (shortcutsShownRef.current) {
        // Only a few keys dismiss it (Esc/? are advertised; Space/Enter too).
        // Other keys pass through, so the user can try shortcut combos while the
        // overlay is up.
        if (ev.key == 'Escape' || ev.key == '?' || ev.key == ' ' || ev.key == 'Enter') {
          ev.preventDefault();
          ev.stopPropagation();
          setShowShortcutsOverlay(false);
        }
        return;
      }
      if (ev.key == '?' && !ev.repeat && !ev.ctrlKey && !ev.metaKey && !ev.altKey && !isTyping()) {
        ev.preventDefault();
        setShowShortcutsOverlay(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  const effectiveLeftWidth = isLeftCollapsed ? 0 : leftWidth;
  const effectiveMiddleWidth = showMiddle && middle && !isMiddleCollapsed
    ? (isRightCollapsed ? undefined : middleWidth) 
    : 0;

  const handleArticleSelect = useCallback(() => {
    if (isMobileScreen) {
      // In mobile mode, show right (PostView), hide middle
      setIsLeftCollapsed(true);
      setIsMiddleCollapsed(true);
      setIsRightCollapsed(false);
    } else if (isNarrowScreen) {
      // In narrow mode, close the drawer; content (middle + right for full feeds) stays shown.
      setIsLeftCollapsed(true);
      setIsRightCollapsed(false);
    }
    onArticleSelect?.();
  }, [isNarrowScreen, isMobileScreen, onArticleSelect]);
  
  const handleFeedSelect = useCallback(() => {
    if (isMobileScreen) {
      // In mobile mode, close drawer and show PostView (middle is hidden on mobile)
      setIsLeftCollapsed(true);
      setIsMiddleCollapsed(true);
      setIsRightCollapsed(false);
      // For cross-page navigation: ensure the next ThreeColumnLayout also starts collapsed
      startCollapsed = true;
      return;
    }
    // In narrow mode, selecting any feed closes the overlay drawer; content fills full width
    // (HeadlineList + PostView for full feeds, PostView otherwise).
    if (isNarrowScreen) {
      setIsLeftCollapsed(true);
      setIsMiddleCollapsed(false);
      setIsRightCollapsed(false);
    }
  }, [isMobileScreen, isNarrowScreen]);

  const handleBackFromRight = useCallback(() => {
    if (isMobileScreen) {
      // In mobile mode, open/close the left drawer instead of switching columns
      toggleLeftCollapse();
    } else {
      toggleLeftCollapse();
    }
  }, [isMobileScreen, toggleLeftCollapse]);

  const leftContent = typeof left === 'function' ? left({ onCollapse: toggleLeftCollapse, onMenuClick: handleFeedSelect, isMobile: isMobileScreen, isDrawer: isNarrowScreen }) : left;
  const middleContent = typeof middle === 'function'
    ? middle({ showExpandButton: isLeftCollapsed, onExpand: toggleLeftCollapse, onArticleSelect: handleArticleSelect, isMobile: isMobileScreen })
    : middle;
  const rightContent = typeof right === 'function'
    ? right({ showExpandButton: isLeftCollapsed, onExpand: handleBackFromRight, isMobile: isMobileScreen })
    : right;
  const dialogContent = typeof dialog === 'function'
    ? dialog({ isMobile: isMobileScreen, onExpand: toggleLeftCollapse })
    : dialog;

  const hasDialog = !!dialog;

  // When the sidebar is an open overlay drawer (narrow/mobile), everything behind it is made
  // `inert` so focus can't reach it — by keyboard (Tab/arrows) or mouse. On desktop the sidebar
  // is a permanent column, so the content stays interactive and focus can leave the sidebar.
  const contentInert = isNarrowScreen && !isLeftCollapsed;

  return (
    <div className="threeColumnLayout" ref={containerRef}>
      {showLeft && (
        <>
          <div
            className={`leftColumn ${isLeftCollapsed ? 'collapsed' : ''}`}
            style={{ width: effectiveLeftWidth }}
          >
            {!isLeftCollapsed && leftContent}
          </div>
          {/* The left resize handle is only meaningful on desktop, where the sidebar is a
              real column. In drawer mode (mobile/narrow) the sidebar overlays the content. */}
          {!isLeftCollapsed && !isNarrowScreen && (
            <div
              ref={leftResizeRef}
              className={`resizeHandle ${isDraggingLeft ? 'dragging' : ''}`}
              onPointerDown={handleLeftResizeStart}
            />
          )}
        </>
      )}

      {!hasDialog && showMiddle && middle && !isMiddleCollapsed && (
        <>
          <div
            className={`middleColumn ${isRightCollapsed ? 'expanded' : ''}`}
            style={{ width: effectiveMiddleWidth }}
            inert={contentInert || undefined}
          >
            {middleContent}
          </div>
          {!isRightCollapsed && (
            <div
              ref={middleResizeRef}
              className={`resizeHandle ${isDraggingMiddle ? 'dragging' : ''}`}
              onPointerDown={handleMiddleResizeStart}
            />
          )}
        </>
      )}

      {(!isRightCollapsed || hasDialog) && (
        <div className={`rightColumn ${hasDialog || isMiddleCollapsed ? 'expanded' : ''}`} inert={contentInert || undefined}>
          {hasDialog ? dialogContent : rightContent}
        </div>
      )}

      {/* Backdrop to close the sidebar when clicking outside, in drawer mode (mobile/narrow).
          isNarrowScreen is true for mobile too. */}
      {isNarrowScreen && !isLeftCollapsed && (
        <div
          className="mobileOverlay"
          onClick={toggleLeftCollapse}
        />
      )}

      {/* Ghost drag lines */}
      {leftGhostPosition !== null && (
        <div
          className="ghostDragLine"
          style={{ left: leftGhostPosition }}
        />
      )}
      {middleGhostPosition !== null && (
        <div
          className="ghostDragLine"
          style={{ left: (isLeftCollapsed ? 0 : leftWidth) + middleGhostPosition }}
        />
      )}
      <ShortcutsOverlay show={showShortcutsOverlay} />
    </div>
  );
};

export default ThreeColumnLayout;
